import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import { getRequest, patchRequest, postRequest } from 'apis/ApiInstence';

const CONTENT_SLUGS = [
    'privacy_policy',
    'terms_conditions',
    'contact_info',
    'about_us',
    'cancellation_policy',
    'payment_policy'
];

const PAGE_LABELS = {
    privacy_policy: 'Privacy Policy',
    terms_conditions: 'Terms & Conditions',
    contact_info: 'Contact Info',
    about_us: 'About Us',
    cancellation_policy: 'Cancellation Policy',
    payment_policy: 'Payment Policy',
    logo: 'Logo'
};

const AUDIENCE_LABELS = {
    customer: 'Customers',
    serviceman: 'Servicemen'
};

const QUILL_MODULES_BASE = {
    toolbar: {
        container: [
            ['bold', 'italic', 'underline'],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, false] }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link', 'image'],
            ['clean']
        ]
    }
};

const StaticPages = () => {
    const [pages, setPages] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(null);
    const [title, setTitle] = useState('');
    const [content, setContent] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const quillRef = useRef(null);

    const loadPages = async () => {
        const res = await getRequest('staticpages');
        if (res?.status) {
            setPages(res.data);
        } else {
            toast.error('Failed to load static pages!');
        }
    };

    useEffect(() => {
        loadPages();
    }, []);

    const imageHandler = useCallback(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('image', file);

            const res = await postRequest('staticpages/upload-image', formData);
            if (res?.status === 200 && res.data?.url) {
                const quill = quillRef.current?.getEditor();
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'image', res.data.url);
                quill.setSelection(range.index + 1);
            } else {
                toast.error('Image upload failed!');
            }
        };
    }, []);

    const quillModules = React.useMemo(
        () => ({
            toolbar: {
                ...QUILL_MODULES_BASE.toolbar,
                handlers: { image: imageHandler }
            }
        }),
        [imageHandler]
    );

    const handleEditClick = (page) => {
        setCurrentPage(page);
        setTitle(page.title || '');
        setContent(page.content || '');
        setImageFile(null);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setCurrentPage(null);
        setImageFile(null);
    };

    const handleSave = async () => {
        if (!currentPage) return;
        setSaving(true);

        let payload;
        if (currentPage.slug === 'logo' && imageFile) {
            payload = new FormData();
            payload.append('title', title);
            payload.append('image', imageFile);
        } else {
            payload = { title, content };
        }

        const res = await patchRequest(`staticpages/${currentPage.slug}/${currentPage.audience}`, payload);
        setSaving(false);

        if (res?.status) {
            toast.success('Static page updated! Customers and servicemen have been notified.');
            handleCloseModal();
            loadPages();
        } else {
            toast.error('Failed to update static page!');
        }
    };

    const logoPage = pages.find((p) => p.slug === 'logo');

    return (
        <>
            <div className="container-fluid mt-5">
                <div className="mainheadig mx-4">
                    <h4 className="text-white font-weight-bold">Static Pages</h4>
                </div>

                {CONTENT_SLUGS.map((slug) => {
                    const audienceRows = pages.filter((p) => p.slug === slug);
                    if (!audienceRows.length) return null;

                    return (
                        <div className="mx-2 mb-4" key={slug}>
                            <h5 className="mx-2 mb-3">{PAGE_LABELS[slug]}</h5>
                            <div className="row">
                                {audienceRows.map((page) => (
                                    <div className="col-md-6 col-lg-4 mb-3" key={`${page.slug}-${page.audience}`}>
                                        <div className="card h-100">
                                            <div className="card-body d-flex flex-column">
                                                <span className="badge badge-info mb-2" style={{ alignSelf: 'flex-start' }}>
                                                    {AUDIENCE_LABELS[page.audience] || page.audience}
                                                </span>
                                                <p className="card-text text-muted small" style={{ whiteSpace: 'pre-line' }}>
                                                    {page.content
                                                        ? `${page.content.replace(/<[^>]+>/g, ' ').trim().slice(0, 120)}…`
                                                        : 'No content yet'}
                                                </p>
                                                <button
                                                    className="btn editbtn mt-auto"
                                                    onClick={() => handleEditClick(page)}
                                                >
                                                    Edit
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {logoPage && (
                    <div className="mx-2 mb-4">
                        <h5 className="mx-2 mb-3">Logo</h5>
                        <div className="row">
                            <div className="col-md-6 col-lg-4 mb-3">
                                <div className="card h-100">
                                    {logoPage.image && (
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND}asserts/staticpages/${logoPage.image}`}
                                            alt="Logo"
                                            className="card-img-top p-3"
                                            style={{ height: '120px', objectFit: 'contain' }}
                                        />
                                    )}
                                    <div className="card-body d-flex flex-column">
                                        <button
                                            className="btn editbtn mt-auto"
                                            onClick={() => handleEditClick(logoPage)}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showModal && currentPage && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Edit {PAGE_LABELS[currentPage.slug]}
                                    {currentPage.slug !== 'logo' && ` — ${AUDIENCE_LABELS[currentPage.audience]}`}
                                </h5>
                                <button type="button" className="close" onClick={handleCloseModal}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <form>
                                    {currentPage.slug === 'logo' ? (
                                        <div className="form-group">
                                            <label htmlFor="logoImage">Logo Image</label>
                                            {currentPage.image && (
                                                <div className="mb-2">
                                                    <img
                                                        src={`${process.env.REACT_APP_BACKEND}asserts/staticpages/${currentPage.image}`}
                                                        alt="Current logo"
                                                        style={{ height: '80px', objectFit: 'contain' }}
                                                    />
                                                </div>
                                            )}
                                            <input
                                                type="file"
                                                id="logoImage"
                                                className="form-control"
                                                accept="image/*"
                                                onChange={(e) => setImageFile(e.target.files[0] || null)}
                                            />
                                        </div>
                                    ) : (
                                        <>
                                            <div className="form-group">
                                                <label htmlFor="pageTitle">Title</label>
                                                <input
                                                    type="text"
                                                    id="pageTitle"
                                                    className="form-control"
                                                    value={title}
                                                    onChange={(e) => setTitle(e.target.value)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- ReactQuill doesn't render a labelable control the linter can see */}
                                                <label id="pageContentLabel">Content</label>
                                                <ReactQuill
                                                    aria-labelledby="pageContentLabel"
                                                    ref={quillRef}
                                                    theme="snow"
                                                    value={content}
                                                    onChange={setContent}
                                                    modules={quillModules}
                                                    style={{ height: '260px', marginBottom: '42px' }}
                                                />
                                            </div>
                                        </>
                                    )}
                                </form>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                    Close
                                </button>
                                <button type="button" className="btn btn-primary" disabled={saving} onClick={handleSave}>
                                    {saving ? 'Saving…' : 'Save changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StaticPages;
